"use client";

import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Menu, X, ChevronRight, ChevronDown } from "lucide-react";
import ApplyButton from "../common/ApplyButton";
import { navigationMap } from "../../route-config";

// Custom CSS for text size adjustments at different breakpoints
const responsiveTextStyles = `
  /* Smaller text at 1250px */
  @media (min-width: 1200px) and (max-width: 1300px) {
    .medium-text-title {
      font-size: 1.5rem !important; /* Smaller title */
    }
    .medium-text-subtitle {
      font-size: 0.7rem !important; /* Smaller subtitle */
    }
    .medium-text-nav {
      font-size: 0.813rem !important; /* Smaller navigation text */
    }
    .medium-icon {
      width: 0.875rem !important;
      height: 0.875rem !important;
    }
    .medium-button-text {
      font-size: 0.813rem !important;
      padding: 0.375rem 0.75rem !important;
    }
  }

  /* Larger text above 1440px */
  @media (min-width: 1541px) {
    .large-text-title {
      font-size: 2.5rem !important; /* Larger title */
    }
    .large-text-subtitle {
      font-size: 1rem !important; /* Larger subtitle */
    }
    .large-text-nav {
      font-size: 1.125rem !important; /* Larger navigation text */
    }
    .large-icon {
      width: 1.25rem !important;
      height: 1.25rem !important;
    }
    .large-button-text {
      font-size: 1.125rem !important;
      padding: 0.625rem 1.25rem !important;
    }
  }
`;

const AnimatedSection = ({ children }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      animate={controls}
      initial="hidden"
      transition={{ duration: 0.5, ease: "easeOut" }}
      variants={{
        visible: { opacity: 1, y: 0 },
        hidden: { opacity: 0, y: 50 },
      }}
    >
      {children}
    </motion.div>
  );
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProgramsOpen, setIsProgramsOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const admissionsDropdownRef = useRef(null);

  // Separate states for desktop and mobile
  const [desktopActiveDropdown, setDesktopActiveDropdown] = useState(null);
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState(null);

  // Add a new state for the Admissions dropdown
  const [isAdmissionsOpen, setIsAdmissionsOpen] = useState(false);
  const [admissionsActiveDropdown, setAdmissionsActiveDropdown] =
    useState(null);
  const [mobileAdmissionsActiveDropdown, setMobileAdmissionsActiveDropdown] =
    useState(null);

  // Get navigation items from the navigation map
  const {
    main: mainRoutes,
    programs: programRoutes,
    admissions: admissionsRoutes,
    other: otherRoutes,
  } = navigationMap;

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsProgramsOpen(false);
    setIsAdmissionsOpen(false);
    setDesktopActiveDropdown(null);
    setMobileActiveDropdown(null);
    setAdmissionsActiveDropdown(null);
    setMobileAdmissionsActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProgramsOpen(false);
        setDesktopActiveDropdown(null);
      }
      if (
        admissionsDropdownRef.current &&
        !admissionsDropdownRef.current.contains(event.target)
      ) {
        setIsAdmissionsOpen(false);
        setAdmissionsActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isProgramsActive = () => {
    const programPaths = programRoutes.flatMap((item) =>
      item.type === "dropdown"
        ? item.programs.map((program) => program.path)
        : [item.path]
    );
    return programPaths.some((path) => location.pathname === path);
  };

  // Add a new function to check if any admissions paths are active
  const isAdmissionsActive = () => {
    const admissionPaths = admissionsRoutes.flatMap((item) =>
      item.programs.map((program) => program.path)
    );
    return admissionPaths.some((path) => location.pathname === path);
  };

  const isScrolled = scrollPosition > 50;

  return (
    <>
      {/* Inject custom CSS for responsive text sizes */}
      <style dangerouslySetInnerHTML={{ __html: responsiveTextStyles }} />

      {/* NavHeader */}
      <motion.header
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-sm shadow-lg"
            : "bg-transparent"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img className="h-12 w-auto" src="/logo.png" alt="NASTP Logo" />
              <div className="flex flex-col">
                <span
                  className={`text-2xl font-bold medium-text-title large-text-title ${
                    isScrolled
                      ? "bg-gradient-to-r from-blue-700 to-indigo-800 bg-clip-text text-transparent"
                      : "text-white"
                  }`}
                >
                  NIIT
                </span>
                <span
                  className={`text-xs medium-text-subtitle large-text-subtitle ${
                    isScrolled ? "text-gray-500" : "text-white/80"
                  } hidden sm:block`}
                >
                  NASTP Institute of Information Technology
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {mainRoutes.map(({ name, path }) => (
                <Link
                  key={name}
                  to={path}
                  className={`px-3 py-2 rounded-md transition-all duration-200 text-sm font-medium medium-text-nav large-text-nav ${
                    isActive(path)
                      ? isScrolled
                        ? "bg-blue-700 text-white"
                        : "bg-white/20 text-white"
                      : isScrolled
                      ? "text-gray-700 hover:text-blue-700 hover:bg-blue-50"
                      : "text-white hover:bg-white/10"
                  }`}
                >
                  {name}
                </Link>
              ))}

              {/* Programs Dropdown - Desktop */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsProgramsOpen(!isProgramsOpen)}
                  onMouseEnter={() => setIsProgramsOpen(true)}
                  className={`px-3 py-2 rounded-md transition-all duration-200 text-sm font-medium medium-text-nav large-text-nav flex items-center ${
                    isProgramsActive()
                      ? isScrolled
                        ? "bg-blue-700 text-white"
                        : "bg-white/20 text-white"
                      : isScrolled
                      ? "text-gray-700 hover:text-blue-700 hover:bg-blue-50"
                      : "text-white hover:bg-white/10"
                  }`}
                >
                  Academics{" "}
                  <ChevronDown className="w-4 h-4 ml-1 medium-icon large-icon" />
                </button>

                {isProgramsOpen && (
                  <div
                    className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                    onMouseLeave={() => {
                      setIsProgramsOpen(false);
                      setDesktopActiveDropdown(null);
                    }}
                  >
                    <div className="py-1">
                      {programRoutes.map((item, index) => (
                        <div
                          key={item.name}
                          className="relative"
                          onMouseEnter={() => setDesktopActiveDropdown(index)}
                        >
                          {item.type === "dropdown" ? (
                            <>
                              <div className="block px-4 py-3 text-sm medium-text-nav large-text-nav text-gray-700 hover:bg-gray-50 hover:text-blue-700 cursor-pointer flex justify-between items-center">
                                <div className="font-medium">{item.name}</div>
                                <ChevronRight className="w-4 h-4 medium-icon large-icon" />

                                {desktopActiveDropdown === index && (
                                  <div className="absolute left-full top-0 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 -mt-1">
                                    <div className="py-1">
                                      {item.programs.map((program) => (
                                        <Link
                                          key={program.name}
                                          to={program.path}
                                          className={`block px-4 py-3 text-sm medium-text-nav large-text-nav ${
                                            isActive(program.path)
                                              ? "bg-blue-50 text-blue-700 font-medium"
                                              : "text-gray-700 hover:bg-gray-50 hover:text-blue-700"
                                          }`}
                                          onClick={() => {
                                            setIsProgramsOpen(false);
                                            setDesktopActiveDropdown(null);
                                          }}
                                        >
                                          <div className="font-medium">
                                            {program.name}
                                          </div>
                                          <div className="text-xs medium-text-subtitle large-text-subtitle text-gray-500 mt-0.5">
                                            {program.description}
                                          </div>
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          ) : item.type === "link" ? (
                            <Link
                              to={item.path}
                              className={`block px-4 py-3 text-sm medium-text-nav large-text-nav ${
                                isActive(item.path)
                                  ? "bg-blue-50 text-blue-700 font-medium"
                                  : "text-gray-700 hover:bg-gray-50 hover:text-blue-700"
                              }`}
                              onClick={() => setIsProgramsOpen(false)}
                            >
                              <div className="font-medium">{item.name}</div>
                              <div className="text-xs medium-text-subtitle large-text-subtitle text-gray-500 mt-0.5">
                                {item.description}
                              </div>
                            </Link>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Admissions Menu Dropdown - Desktop */}
              <div className="relative" ref={admissionsDropdownRef}>
                <button
                  onClick={() => setIsAdmissionsOpen(!isAdmissionsOpen)}
                  onMouseEnter={() => setIsAdmissionsOpen(true)}
                  className={`px-3 py-2 rounded-md transition-all duration-200 text-sm font-medium medium-text-nav large-text-nav flex items-center ${
                    isAdmissionsActive()
                      ? isScrolled
                        ? "bg-blue-700 text-white"
                        : "bg-white/20 text-white"
                      : isScrolled
                      ? "text-gray-700 hover:text-blue-700 hover:bg-blue-50"
                      : "text-white hover:bg-white/10"
                  }`}
                >
                  Admissions Menu{" "}
                  <ChevronDown className="w-4 h-4 ml-1 medium-icon large-icon" />
                </button>

                {isAdmissionsOpen && (
                  <div
                    className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                    onMouseLeave={() => {
                      setIsAdmissionsOpen(false);
                      setAdmissionsActiveDropdown(null);
                    }}
                  >
                    <div className="py-1">
                      {admissionsRoutes.map((item, index) => (
                        <div
                          key={item.name}
                          className="relative"
                          onMouseEnter={() =>
                            setAdmissionsActiveDropdown(index)
                          }
                        >
                          <div className="block px-4 py-3 text-sm medium-text-nav large-text-nav text-gray-700 hover:bg-gray-50 hover:text-blue-700 cursor-pointer flex justify-between items-center">
                            <div className="font-medium">{item.name}</div>
                            <ChevronRight className="w-4 h-4 medium-icon large-icon" />

                            {admissionsActiveDropdown === index && (
                              <div className="absolute left-full top-0 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 -mt-1">
                                <div className="py-1">
                                  {item.programs.map((program) => (
                                    <Link
                                      key={program.name}
                                      to={program.path}
                                      className={`block px-4 py-3 text-sm medium-text-nav large-text-nav ${
                                        isActive(program.path)
                                          ? "bg-blue-50 text-blue-700 font-medium"
                                          : "text-gray-700 hover:bg-gray-50 hover:text-blue-700"
                                      }`}
                                      onClick={() => {
                                        setIsAdmissionsOpen(false);
                                        setAdmissionsActiveDropdown(null);
                                      }}
                                    >
                                      <div className="font-medium">
                                        {program.name}
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {otherRoutes.map(({ name, path }) => (
                <Link
                  key={name}
                  to={path}
                  target={path.startsWith("http") ? "_blank" : undefined}
                  className={`px-3 py-2 rounded-md transition-all duration-200 text-sm font-medium medium-text-nav large-text-nav ${
                    isActive(path)
                      ? isScrolled
                        ? "bg-blue-700 text-white"
                        : "bg-white/20 text-white"
                      : isScrolled
                      ? "text-gray-700 hover:text-blue-700 hover:bg-blue-50"
                      : "text-white hover:bg-white/10"
                  }`}
                >
                  {name}
                </Link>
              ))}
              <ApplyButton
                to="/sign-in"
                className={`ml-2 px-4 py-2 rounded-full text-sm font-medium medium-button-text large-button-text shadow-md hover:shadow-lg duration-300 flex items-center ${
                  isScrolled
                    ? "bg-blue-700 text-white hover:bg-blue-800"
                    : "bg-white text-blue-700 hover:bg-white/90"
                }`}
              >
                Apply Now{" "}
                <ChevronRight className="w-4 h-4 ml-1 medium-icon large-icon" />
              </ApplyButton>
            </nav>

            {/* Mobile Menu Toggle Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden focus:outline-none"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X
                  className={`h-6 w-6 ${
                    isScrolled ? "text-gray-800" : "text-white"
                  }`}
                />
              ) : (
                <Menu
                  className={`h-6 w-6 ${
                    isScrolled ? "text-gray-800" : "text-white"
                  }`}
                />
              )}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 bg-white/95 backdrop-blur-sm lg:hidden pt-20 overflow-y-auto">
          <div className="flex flex-col items-center justify-start h-full space-y-2 p-8 pb-20">
            {mainRoutes.map(({ name, path }) => (
              <Link
                key={name}
                to={path}
                className={`w-full text-center py-4 border-b border-gray-100 text-lg font-medium ${
                  isActive(path)
                    ? "text-blue-700 font-semibold"
                    : "text-gray-800 hover:text-blue-700"
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {name}
              </Link>
            ))}

            {/* Programs Dropdown - Mobile */}
            <div className="w-full">
              <button
                onClick={() => setIsProgramsOpen(!isProgramsOpen)}
                className={`w-full text-center py-4 border-b border-gray-100 text-lg font-medium flex items-center justify-center ${
                  isProgramsActive()
                    ? "text-blue-700 font-semibold"
                    : "text-gray-800 hover:text-blue-700"
                }`}
              >
                Academics
                <ChevronDown
                  className={`w-4 h-4 ml-1 transition-transform duration-200 ${
                    isProgramsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isProgramsOpen && (
                <div className="bg-gray-50 rounded-md my-2">
                  {programRoutes.map((item, index) => (
                    <div key={item.name}>
                      {item.type === "dropdown" ? (
                        <>
                          <div
                            className="flex items-center justify-between px-6 py-3 text-base font-medium text-gray-800 hover:text-blue-700 cursor-pointer"
                            onClick={() =>
                              setMobileActiveDropdown(
                                mobileActiveDropdown === index ? null : index
                              )
                            }
                          >
                            {item.name}
                            <ChevronDown
                              className={`w-4 h-4 ml-1 transition-transform duration-200 ${
                                mobileActiveDropdown === index
                                  ? "rotate-180"
                                  : ""
                              }`}
                            />
                          </div>

                          {mobileActiveDropdown === index && (
                            <div className="pl-8 bg-gray-100 rounded-md my-1">
                              {item.programs.map((program) => (
                                <Link
                                  key={program.name}
                                  to={program.path}
                                  className={`block px-6 py-3 text-base ${
                                    isActive(program.path)
                                      ? "text-blue-700 font-medium"
                                      : "text-gray-700 hover:text-blue-700"
                                  }`}
                                  onClick={() => {
                                    setIsMenuOpen(false);
                                    setIsProgramsOpen(false);
                                    setMobileActiveDropdown(null);
                                  }}
                                >
                                  <div className="font-medium">
                                    {program.name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {program.description}
                                  </div>
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : item.type === "link" ? (
                        <Link
                          to={item.path}
                          className={`block px-6 py-3 text-base ${
                            isActive(item.path)
                              ? "text-blue-700 font-medium"
                              : "text-gray-700 hover:text-blue-700"
                          }`}
                          onClick={() => {
                            setIsMenuOpen(false);
                            setIsProgramsOpen(false);
                          }}
                        >
                          <div className="font-medium">{item.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {item.description}
                          </div>
                        </Link>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Admissions Menu Dropdown - Mobile */}
            <div className="w-full">
              <button
                onClick={() => setIsAdmissionsOpen(!isAdmissionsOpen)}
                className={`w-full text-center py-4 border-b border-gray-100 text-lg font-medium flex items-center justify-center ${
                  isAdmissionsActive()
                    ? "text-blue-700 font-semibold"
                    : "text-gray-800 hover:text-blue-700"
                }`}
              >
                Admissions Menu
                <ChevronDown
                  className={`w-4 h-4 ml-1 transition-transform duration-200 ${
                    isAdmissionsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isAdmissionsOpen && (
                <div className="bg-gray-50 rounded-md my-2">
                  {admissionsRoutes.map((item, index) => (
                    <div key={item.name}>
                      <div
                        className="flex items-center justify-between px-6 py-3 text-base font-medium text-gray-800 hover:text-blue-700 cursor-pointer"
                        onClick={() =>
                          setMobileAdmissionsActiveDropdown(
                            mobileAdmissionsActiveDropdown === index
                              ? null
                              : index
                          )
                        }
                      >
                        {item.name}
                        <ChevronDown
                          className={`w-4 h-4 ml-1 transition-transform duration-200 ${
                            mobileAdmissionsActiveDropdown === index
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </div>

                      {mobileAdmissionsActiveDropdown === index && (
                        <div className="pl-8 bg-gray-100 rounded-md my-1">
                          {item.programs.map((program) => (
                            <Link
                              key={program.name}
                              to={program.path}
                              className={`block px-6 py-3 text-base ${
                                isActive(program.path)
                                  ? "text-blue-700 font-medium"
                                  : "text-gray-700 hover:text-blue-700"
                              }`}
                              onClick={() => {
                                setIsMenuOpen(false);
                                setIsAdmissionsOpen(false);
                                setMobileAdmissionsActiveDropdown(null);
                              }}
                            >
                              <div className="font-medium">{program.name}</div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {otherRoutes.map(({ name, path }) => (
              <Link
                key={name}
                to={path}
                target={path.startsWith("http") ? "_blank" : undefined}
                className={`w-full text-center py-4 border-b border-gray-100 text-lg font-medium ${
                  isActive(path)
                    ? "text-blue-700 font-semibold"
                    : "text-gray-800 hover:text-blue-700"
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {name}
              </Link>
            ))}
            <div className="mt-3 space-y-1 px-2 w-full">
              <ApplyButton
                to="/sign-in"
                className="w-full text-center block rounded-md px-3 py-3 text-base font-medium bg-blue-600 text-white hover:bg-blue-700"
              >
                Apply Now <ChevronRight className="w-4 h-4 ml-1 inline" />
              </ApplyButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
